import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import { SettingsState, AppSettings } from '@/types'

const initialState: SettingsState = {
  settings: {
    language: 'ar',
    theme: 'light',
    currency: 'SAR',
    tax_rate: 0.15,
    timezone: 'Asia/Riyadh',
    date_format: 'DD/MM/YYYY',
    time_format: '24h',
    notifications: {
      order_alerts: true,
      payment_alerts: true,
      shift_alerts: true,
      sound_enabled: true,
    },
    printer: {
      enabled: false,
      printer_name: undefined,
      paper_size: 'thermal',
      auto_print: false,
    },
    pos: {
      auto_calculate_tax: true,
      require_customer: false,
      allow_discounts: true,
      max_discount_percentage: 50,
      default_order_type: 'dine_in',
    },
  },
  orderTaxPercentage: null,
  orderServiceFeePercentage: null,
  isLoading: false,
  error: null,
}

// Async thunks
export const loadSettings = createAsyncThunk(
  'settings/loadSettings',
  async (_, { rejectWithValue }) => {
    try {
      // Load settings from localStorage
      const savedSettings = localStorage.getItem('app_settings')
      if (savedSettings) {
        const settings = JSON.parse(savedSettings)
        // Ensure language is also in localStorage for API access
        if (settings.language) {
          localStorage.setItem('language', settings.language)
        }
        return settings
      }
      // If no saved settings, initialize with default and save to localStorage
      const defaultSettings = initialState.settings
      localStorage.setItem('language', defaultSettings.language)
      localStorage.setItem('app_settings', JSON.stringify(defaultSettings))
      return defaultSettings
    } catch (error: any) {
      return rejectWithValue('Failed to load settings')
    }
  }
)

export const saveSettings = createAsyncThunk(
  'settings/saveSettings',
  async (settings: AppSettings, { rejectWithValue }) => {
    try {
      localStorage.setItem('app_settings', JSON.stringify(settings))
      // Also save language separately for API access
      if (settings.language) {
        localStorage.setItem('language', settings.language)
      }
      return settings
    } catch (error: any) {
      return rejectWithValue('Failed to save settings')
    }
  }
)

const settingsSlice = createSlice({
  name: 'settings',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null
    },
    updateSettings: (state, action: PayloadAction<Partial<AppSettings>>) => {
      state.settings = { ...state.settings, ...action.payload }
      // If language is being updated, also save to localStorage
      if (action.payload.language) {
        localStorage.setItem('language', action.payload.language)
      }
      // Update app_settings in localStorage
      localStorage.setItem('app_settings', JSON.stringify(state.settings))
    },
    setLanguage: (state, action: PayloadAction<'ar' | 'en'>) => {
      state.settings.language = action.payload
      // Also save to localStorage for API access
      localStorage.setItem('language', action.payload)
      // Update app_settings
      const appSettings = { ...state.settings, language: action.payload }
      localStorage.setItem('app_settings', JSON.stringify(appSettings))
    },
    setTheme: (state, action: PayloadAction<'light' | 'dark'>) => {
      state.settings.theme = action.payload
    },
    setCurrency: (state, action: PayloadAction<string>) => {
      state.settings.currency = action.payload
    },
    setTaxRate: (state, action: PayloadAction<number>) => {
      state.settings.tax_rate = action.payload
    },
    setOrderTaxPercentage: (state, action: PayloadAction<number | null>) => {
      state.orderTaxPercentage = action.payload
    },
    setOrderServiceFeePercentage: (state, action: PayloadAction<number | null>) => {
      state.orderServiceFeePercentage = action.payload
    },
    updateNotifications: (state, action: PayloadAction<Partial<AppSettings['notifications']>>) => {
      state.settings.notifications = { ...state.settings.notifications, ...action.payload }
    },
    updatePrinter: (state, action: PayloadAction<Partial<AppSettings['printer']>>) => {
      state.settings.printer = { ...state.settings.printer, ...action.payload }
    },
    updatePOS: (state, action: PayloadAction<Partial<AppSettings['pos']>>) => {
      state.settings.pos = { ...state.settings.pos, ...action.payload }
    },
  },
  extraReducers: (builder) => {
    builder
      // Load settings
      .addCase(loadSettings.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(loadSettings.fulfilled, (state, action: PayloadAction<AppSettings>) => {
        state.isLoading = false
        state.settings = action.payload
        state.error = null
        // Ensure language is also saved in localStorage for API access
        if (action.payload.language) {
          localStorage.setItem('language', action.payload.language)
        }
      })
      .addCase(loadSettings.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })
      
      // Save settings
      .addCase(saveSettings.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(saveSettings.fulfilled, (state, action: PayloadAction<AppSettings>) => {
        state.isLoading = false
        state.settings = action.payload
        state.error = null
      })
      .addCase(saveSettings.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })
  },
})

export const {
  clearError,
  updateSettings,
  setLanguage,
  setTheme,
  setCurrency,
  setTaxRate,
  setOrderTaxPercentage,
  setOrderServiceFeePercentage,
  updateNotifications,
  updatePrinter,
  updatePOS,
} = settingsSlice.actions

export default settingsSlice.reducer
